import { useCallback, useEffect, useState } from "react";
import {
  Plus, Trash2, Loader2, Pencil, FileDown, Mail, Bell, Eye, Archive, ArchiveRestore, ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);

const blankInvoice = () => ({
  doc_type: "invoice",
  client_id: "",
  client_name: "",
  client_email: "",
  client_address: "",
  issue_date: today(),
  due_date: "",
  status: "draft",
  notes: "",
  event_id: "",
  items: [{ description: "", quantity: 1, unit_price: 0 }],
});

const INVOICE_STATUSES = ["draft", "sent", "paid", "overdue"];
const QUOTE_STATUSES = ["draft", "sent", "accepted", "declined"];

const statusColor = {
  draft: "bg-white/10 text-white/50 border-white/20",
  sent: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  overdue: "bg-red-500/15 text-red-400 border-red-500/30",
  accepted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  declined: "bg-red-500/15 text-red-400 border-red-500/30",
};

const rowBarColor = {
  draft: "border-l-gray-500",
  sent: "border-l-amber-500",
  paid: "border-l-emerald-500",
  overdue: "border-l-red-500",
  accepted: "border-l-emerald-500",
  declined: "border-l-red-500",
};

const gbp = (x) => `£${(Number(x) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const NSelect = ({ value, onChange, options, testid, placeholder, className = "" }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className={`h-9 w-full appearance-none rounded-md border border-brand-line bg-brand-obsidian px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${value ? "text-white" : "text-white/50"} ${className}`}
  >
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export default function BillingTab() {
  const [invoices, setInvoices] = useState(null);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [sub, setSub] = useState("outstanding");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewInv, setViewInv] = useState(null);
  const [form, setForm] = useState(blankInvoice());
  const [busy, setBusy] = useState(false);
  const [emailingId, setEmailingId] = useState(null);

  const load = useCallback(() => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
    api.get("/line-items").then((r) => setCatalog(r.data)).catch(() => setCatalog([]));
  }, []);
  useEffect(load, []);

  const typeOf = (i) => i.doc_type || "invoice";
  const lists = invoices
    ? {
        outstanding: invoices.filter((i) => typeOf(i) === "invoice" && !i.archived && ["sent", "overdue"].includes(i.status)),
        paid: invoices.filter((i) => typeOf(i) === "invoice" && !i.archived && i.status === "paid"),
        new: invoices.filter((i) => typeOf(i) === "invoice" && !i.archived && i.status === "draft"),
        quotes: invoices.filter((i) => typeOf(i) === "quote" && !i.archived),
        archived: invoices.filter((i) => i.archived),
      }
    : null;

  const stats = invoices
    ? {
        outstanding: lists.outstanding.reduce((s, i) => s + (i.total || 0), 0),
        overdue: lists.outstanding.filter((i) => i.status === "overdue").length,
        paid: lists.paid.reduce((s, i) => s + (i.total || 0), 0),
        quotes: lists.quotes.reduce((s, i) => s + (i.total || 0), 0),
      }
    : null;

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setItem = (idx, key, value) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)) }));

  const pickClient = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      client_id: id,
      client_name: c ? c.name : f.client_name,
      client_email: c ? c.email : f.client_email,
      client_address: c ? c.address : f.client_address,
    }));
  };

  const pickEvent = (id) => {
    const ev = events.find((x) => x.id === id);
    if (!ev || form.event_id === id) return;
    const client = clients.find((c) => c.id === ev.client_id);
    setForm((f) => ({
      ...f,
      event_id: id,
      client_id: client ? client.id : f.client_id,
      client_name: client ? client.name : f.client_name,
      client_email: client ? client.email : f.client_email,
      client_address: client ? client.address : f.client_address,
      items: [
        ...f.items.filter((it) => it.description.trim()),
        { description: [ev.title, ev.dog, ev.date].filter(Boolean).join(" - "), quantity: 1, unit_price: 0 },
      ],
    }));
  };

  const addCatalogItem = (id) => {
    const c = catalog.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, items: [...f.items.filter((it) => it.description.trim() || f.items.length === 1), { description: c.name, quantity: 1, unit_price: c.unit_price }] }));
  };

  const openNew = (docType = "invoice") => {
    setEditing(null);
    setForm({ ...blankInvoice(), doc_type: docType });
    setDialogOpen(true);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      doc_type: typeOf(inv),
      client_id: inv.client_id || "",
      client_name: inv.client_name || "",
      client_email: inv.client_email || "",
      client_address: inv.client_address || "",
      issue_date: inv.issue_date || "",
      due_date: inv.due_date || "",
      status: inv.status || "draft",
      notes: inv.notes || "",
      event_id: inv.event_id || "",
      items: inv.items?.length ? inv.items : [{ description: "", quantity: 1, unit_price: 0 }],
    });
    setDialogOpen(true);
  };

  const total = (items) => items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);

  const save = async () => {
    if (!form.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/invoices/${editing.id}`, form);
        toast.success(`${editing.number} updated`);
      } else {
        const { data } = await api.post("/invoices", form);
        toast.success(`${data.number} created`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/invoices/${id}/status`, { status });
      setInvoices((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Could not update", { description: formatApiError(e) });
    }
  };

  const archive = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.id}/archive`, { archived: !inv.archived });
      toast.success(inv.archived ? "Restored" : "Archived");
      load();
    } catch (e) {
      toast.error("Could not archive", { description: formatApiError(e) });
    }
  };

  const convert = async (inv) => {
    try {
      const { data } = await api.patch(`/invoices/${inv.id}/type`, { doc_type: typeOf(inv) === "quote" ? "invoice" : "quote" });
      toast.success(typeOf(inv) === "quote" ? `Quote converted to invoice ${data.number}` : `Moved back to quotes as ${data.number}`);
      load();
    } catch (e) {
      toast.error("Could not convert", { description: formatApiError(e) });
    }
  };

  const remove = async (inv) => {
    try {
      await api.delete(`/invoices/${inv.id}`);
      setInvoices((prev) => prev.filter((i) => i.id !== inv.id));
      toast.success(`${inv.number} deleted`);
    } catch (e) {
      toast.error("Could not delete", { description: formatApiError(e) });
    }
  };

  const saveBlob = (inv, blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inv.number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      saveBlob(inv, res.data);
      toast.success(`${inv.number}.pdf downloaded`);
    } catch (e) {
      if (e?.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
          saveBlob(inv, res.data);
          toast.success(`${inv.number}.pdf downloaded`);
        } catch {
          toast.error("Session expired - please log in again");
        }
        return;
      }
      toast.error("Could not generate PDF", { description: formatApiError(e) });
    }
  };

  const sendEmail = async (inv, mode) => {
    if (!inv.client_email) {
      toast.error("No client email on this invoice", { description: "Edit it and add the client's email address." });
      return;
    }
    setEmailingId(inv.id);
    try {
      await api.post(`/invoices/${inv.id}/${mode}`);
      toast.success(mode === "chase" ? `Chase email sent to ${inv.client_email}` : `Emailed to ${inv.client_email}`, {
        description: "The PDF is attached - replies come to hello@questk9.co.uk.",
      });
      load();
    } catch (e) {
      toast.error(mode === "chase" ? "Could not send chase" : "Could not email invoice", { description: formatApiError(e) });
    } finally {
      setEmailingId(null);
    }
  };

  if (invoices === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="billing-loading-spinner" />
      </div>
    );
  }

  const subTabs = [
    { key: "outstanding", label: "Outstanding", count: lists.outstanding.length, cls: "text-amber-400" },
    { key: "paid", label: "Paid", count: lists.paid.length, cls: "text-emerald-400" },
    { key: "new", label: "New Invoices", count: lists.new.length, cls: "text-white/70" },
    { key: "quotes", label: "Quotes", count: lists.quotes.length, cls: "text-brand-gold" },
    { key: "archived", label: "Archived", count: lists.archived.length, cls: "text-white/40" },
  ];
  const visible = lists[sub];
  const statusList = form.doc_type === "quote" ? QUOTE_STATUSES : INVOICE_STATUSES;

  return (
    <div data-testid="billing-tab">
      <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="billing-stats">
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold">Outstanding</p>
          <p className="mt-1 font-display text-2xl font-bold text-white" data-testid="billing-stat-outstanding">{gbp(stats.outstanding)}</p>
          {stats.overdue > 0 && <p className="text-xs text-red-400" data-testid="billing-stat-overdue">{stats.overdue} overdue - chase them</p>}
        </div>
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
          <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-bold">Paid</p>
          <p className="mt-1 font-display text-2xl font-bold text-white" data-testid="billing-stat-paid">{gbp(stats.paid)}</p>
        </div>
        <div className="rounded-lg border border-brand-gold/30 bg-brand-gold/5 p-4">
          <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-gold font-bold">Quotes</p>
          <p className="mt-1 font-display text-2xl font-bold text-white" data-testid="billing-stat-quotes">{gbp(stats.quotes)}</p>
        </div>
      </div>

      <p className="text-xs text-white/40 -mt-1 mb-4" data-testid="billing-lifecycle-hint">
        Lifecycle: create an invoice and it sits in New Invoices → email it (envelope button) and it moves to Outstanding → mark it paid and it moves to Paid.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="billing-subtabs">
        {subTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setSub(t.key)}
            data-testid={`billing-subtab-${t.key}`}
            className={`font-eyebrow text-[11px] uppercase tracking-[0.15em] font-bold px-4 py-2 rounded-md border transition-all ${
              sub === t.key ? "bg-brand-crimson border-brand-crimson text-white" : "border-brand-line text-white/60 hover:text-white"
            }`}
          >
            {t.label} <span className={sub === t.key ? "" : t.cls}>({t.count})</span>
          </button>
        ))}
        <Button onClick={() => openNew("invoice")} data-testid="admin-create-invoice-button" className="ml-auto bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
          <Plus className="h-4 w-4" /> New Invoice
        </Button>
        <Button onClick={() => openNew("quote")} data-testid="admin-create-quote-button" className="bg-transparent hover:bg-brand-gold/10 text-brand-gold border border-brand-gold/50 font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold">
          <Plus className="h-4 w-4" /> New Quote
        </Button>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center text-white/50" data-testid="billing-empty">
          Nothing in "{subTabs.find((t) => t.key === sub)?.label}" right now.
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((inv) => {
            const t = typeOf(inv);
            return (
              <div
                key={inv.id}
                className={`rounded-lg border border-brand-line bg-brand-surface p-5 flex flex-wrap items-center justify-between gap-4 hover:border-brand-crimson/50 transition-colors border-l-4 ${rowBarColor[inv.status] || "border-l-gray-500"}`}
                data-testid={`billing-row-${inv.id}`}
              >
                <div>
                  <div className="flex items-center gap-3">
                    <p className="font-display font-bold text-white" data-testid={`billing-number-${inv.id}`}>{inv.number}</p>
                    {t === "quote" && <span className="text-[10px] font-eyebrow uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full border bg-brand-gold/15 text-brand-gold border-brand-gold/30">Quote</span>}
                    <span className={`text-[10px] font-eyebrow uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full border ${statusColor[inv.status] || statusColor.draft}`} data-testid={`billing-status-${inv.id}`}>{inv.status}</span>
                    {inv.viewed_at && <span className="text-[10px] font-eyebrow uppercase tracking-[0.15em] font-bold text-emerald-400" data-testid={`billing-viewed-${inv.id}`}>viewed {new Date(inv.viewed_at).toLocaleDateString()}</span>}
                    {inv.chased_at && <span className="text-[10px] text-amber-400">chased {new Date(inv.chased_at).toLocaleDateString()}</span>}
                  </div>
                  <p className="mt-1 text-sm text-white/70">{inv.client_name}{inv.client_email && ` • ${inv.client_email}`}</p>
                  <p className="text-xs text-white/40 mt-0.5">Issued {inv.issue_date || "—"} • Due {inv.due_date || "—"} • {inv.items?.length || 0} line item(s){inv.emailed_at ? ` • emailed ${new Date(inv.emailed_at).toLocaleDateString()}` : ""}</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <p className="font-display text-xl font-bold text-white" data-testid={`billing-total-${inv.id}`}>{gbp(inv.total)}</p>
                  <NSelect
                    value={inv.status}
                    onChange={(v) => setStatus(inv.id, v)}
                    options={(t === "quote" ? QUOTE_STATUSES : INVOICE_STATUSES).map((s) => ({ value: s, label: s }))}
                    testid={`billing-status-select-${inv.id}`}
                    className="!w-[105px]"
                  />
                  <button onClick={() => setViewInv(inv)} data-testid={`billing-view-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors" aria-label="View invoice" title="View">
                    <Eye className="h-4 w-4" />
                  </button>
                  {sub === "outstanding" && (
                    <button onClick={() => sendEmail(inv, "chase")} disabled={emailingId === inv.id} data-testid={`billing-chase-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-40" aria-label="Send chase email" title="Send chase">
                      {emailingId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                    </button>
                  )}
                  <button onClick={() => sendEmail(inv, "email")} disabled={emailingId === inv.id} data-testid={`billing-email-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-brand-crimson hover:bg-brand-crimson/10 transition-colors disabled:opacity-40" aria-label="Email to client" title="Email to client">
                    <Mail className="h-4 w-4" />
                  </button>
                  <button onClick={() => downloadPdf(inv)} data-testid={`billing-pdf-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-brand-gold hover:bg-brand-gold/10 transition-colors" aria-label="Download PDF" title="Download PDF">
                    <FileDown className="h-4 w-4" />
                  </button>
                  <button onClick={() => openEdit(inv)} data-testid={`billing-edit-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors" aria-label="Edit" title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => convert(inv)} data-testid={`billing-convert-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-blue-400 hover:bg-blue-500/10 transition-colors" aria-label={t === "quote" ? "Convert to invoice" : "Move to quotes"} title={t === "quote" ? "Convert to invoice" : "Move to quotes"}>
                    <ArrowLeftRight className="h-4 w-4" />
                  </button>
                  <button onClick={() => archive(inv)} data-testid={`billing-archive-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors" aria-label={inv.archived ? "Restore" : "Archive"} title={inv.archived ? "Restore" : "Archive"}>
                    {inv.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  </button>
                  <button onClick={() => remove(inv)} data-testid={`billing-delete-${inv.id}`} className="p-2 rounded-md text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Delete" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="billing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing ? `Edit ${editing.number}` : form.doc_type === "quote" ? "New Quote" : "New Invoice"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Client *</Label>
                <NSelect
                  value={form.client_id}
                  onChange={pickClient}
                  placeholder={clients.length ? "Select a client" : "No clients yet - add one in Clients tab"}
                  options={clients.map((c) => ({ value: c.id, label: c.name }))}
                  testid="billing-client-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Link an event (optional)</Label>
                <NSelect
                  value={form.event_id}
                  onChange={pickEvent}
                  placeholder="None - pick from calendar"
                  options={events.map((e) => ({ value: e.id, label: `${e.title}${e.date ? ` (${e.date})` : ""}` }))}
                  testid="billing-event-select"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Client Email</Label>
                <Input data-testid="billing-client-email" type="email" value={form.client_email} onChange={(e) => setField("client_email", e.target.value)} placeholder="accounts@client.co.uk" className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Client Address</Label>
                <Textarea data-testid="billing-client-address" value={form.client_address} onChange={(e) => setField("client_address", e.target.value)} rows={2} placeholder="Brought in automatically from the client record" className="bg-brand-obsidian border-brand-line" />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Issue Date</Label>
                <Input data-testid="billing-issue-date" type="date" value={form.issue_date} onChange={(e) => setField("issue_date", e.target.value)} className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Due Date</Label>
                <Input data-testid="billing-due-date" type="date" value={form.due_date} onChange={(e) => setField("due_date", e.target.value)} className="bg-brand-obsidian border-brand-line" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">Status</Label>
                <NSelect
                  value={form.status}
                  onChange={(v) => setField("status", v)}
                  options={statusList.map((s) => ({ value: s, label: s }))}
                  testid="billing-status"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-white/70 text-xs">Line Items</Label>
                {catalog.length > 0 && (
                  <NSelect
                    value=""
                    onChange={addCatalogItem}
                    placeholder="+ Add saved item..."
                    options={catalog.map((c) => ({ value: c.id, label: `${c.name} (${gbp(c.unit_price)})` }))}
                    testid="billing-catalog-select"
                    className="!w-[240px] h-8"
                  />
                )}
              </div>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_70px_90px_36px] gap-2 items-center" data-testid={`billing-line-item-${idx}`}>
                  <Input data-testid={`billing-item-description-${idx}`} value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="K9 sweep deployment" className="bg-brand-obsidian border-brand-line" />
                  <Input data-testid={`billing-item-qty-${idx}`} type="number" min="0" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="bg-brand-obsidian border-brand-line" />
                  <Input data-testid={`billing-item-price-${idx}`} type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="bg-brand-obsidian border-brand-line" />
                  <button
                    onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                    disabled={form.items.length === 1}
                    data-testid={`billing-item-remove-${idx}`}
                    className="p-2 rounded-md text-white/40 hover:text-red-400 disabled:opacity-30"
                    aria-label="Remove line item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] }))}
                data-testid="billing-add-line-item"
                className="inline-flex items-center gap-1.5 text-xs font-eyebrow uppercase tracking-[0.15em] font-bold text-brand-crimson hover:text-white transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Blank Line Item
              </button>
              <p className="text-right font-display text-lg font-bold text-white" data-testid="billing-dialog-total">Total: {gbp(total(form.items))}</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/70 text-xs">Notes</Label>
              <Textarea data-testid="billing-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} placeholder="Payment terms, reference, thanks..." className="bg-brand-obsidian border-brand-line" />
            </div>

            <Button onClick={save} disabled={busy} data-testid="billing-save-button" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6">
              {busy ? "Saving..." : editing ? "Save Changes" : form.doc_type === "quote" ? "Create Quote" : "Create Invoice"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewInv} onOpenChange={(o) => !o && setViewInv(null)}>
        <DialogContent className="bg-brand-surface border-brand-line max-w-lg max-h-[85vh] overflow-y-auto" data-testid="billing-view-dialog">
          {viewInv && (
            <div>
              <DialogHeader>
                <DialogTitle className="font-display text-white flex items-center gap-3">
                  {viewInv.number}
                  <span className={`text-[10px] font-eyebrow uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full border ${statusColor[viewInv.status] || statusColor.draft}`}>{viewInv.status}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-1.5 text-sm">
                <p className="text-white font-semibold" data-testid="billing-view-client">{viewInv.client_name}</p>
                {viewInv.client_email && <p className="text-white/50">{viewInv.client_email}</p>}
                {viewInv.client_address && <p className="text-white/50 whitespace-pre-line">{viewInv.client_address}</p>}
              </div>
              <div className="mt-4 rounded-md border border-brand-line divide-y divide-brand-line">
                {(viewInv.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between gap-4 px-4 py-2.5 text-sm">
                    <span className="text-slate-300">{it.description}</span>
                    <span className="text-white font-semibold whitespace-nowrap">{it.quantity} × {gbp(it.unit_price)}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-right font-display text-xl font-bold text-white">Total: {gbp(viewInv.total)}</p>
              {viewInv.notes && <p className="mt-3 text-xs text-white/50">{viewInv.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
